// src/pages/Users/News/hooks/useNewsController.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getNews } from "../../../../services/newsServices";

import {
  DEFAULT_NEWS_UI,
  NEWS_STORAGE_KEY,
  filterAndSortNews,
  getFeaturedNews,
  readNewsStorage,
} from "../utils/newsUtils";

export default function useNewsController({ isManager = false } = {}) {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [ui, setUi] = useState(() => readNewsStorage());
  const [searchInput, setSearchInput] = useState(ui.search || "");

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(next));
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((prev) => {
      const next = {
        ...prev,
        ...patch,
      };

      localStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUi((prev) => {
        if (prev.search === searchInput) return prev;

        const next = {
          ...prev,
          search: searchInput,
          perPage: DEFAULT_NEWS_UI.perPage,
        };

        localStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const refreshNewsList = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getNews();
      setNewsList(Array.isArray(data) ? data.filter(Boolean) : []);
    } catch (err) {
      console.error("Failed to fetch news", err);
      setNewsList([]);
      setError("Impossible de charger les actualités pour le moment.");
      toast.error("Impossible de charger les actualités.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshNewsList();
  }, [refreshNewsList]);

  const filteredSorted = useMemo(
    () => filterAndSortNews(newsList, ui, isManager),
    [newsList, ui, isManager]
  );

  const visible = useMemo(
    () => filteredSorted.slice(0, ui.perPage),
    [filteredSorted, ui.perPage]
  );

  const featuredNews = useMemo(
    () => getFeaturedNews(newsList, isManager, ui.showInactive, 6),
    [newsList, isManager, ui.showInactive]
  );

  const resetFilters = useCallback(() => {
    const next = {
      ...DEFAULT_NEWS_UI,
      showInactive: false,
    };

    persistUi(next);
    setSearchInput(next.search);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    updateUi({
      perPage: ui.perPage + DEFAULT_NEWS_UI.perPage,
    });
  }, [ui.perPage, updateUi]);

  return {
    loading,
    error,
    newsList,
    filteredSorted,
    visible,
    featuredNews,
    ui,
    searchInput,
    setSearchInput,
    updateUi,
    resetFilters,
    loadMore,
    refreshNewsList,
  };
}